import { computed, onBeforeUpdate, onUpdated, ref, Ref } from "@vue/composition-api";
import { Vue } from "vue/types/vue";
import { getElement } from "@/lib/elementHelpers";

interface ScrollPositionOptions extends ScrollOptions {
  keepScroll?: boolean;
  scrollToBottom?: boolean;
}

export default function useKeepCustomScrollPosition(
  scrollNodeRef: Ref<Vue | undefined>,
  defaultOption?: ScrollPositionOptions
) {
  const shouldKeepScroll = ref(defaultOption?.keepScroll ?? false);
  const shouldScrollToBottom = ref(defaultOption?.scrollToBottom ?? false);

  const scrollNode = computed<HTMLElement | undefined>(() => {
    if (!scrollNodeRef.value) return undefined;
    if (scrollNodeRef.value?.$refs?.customScroll && "SimpleBar" in scrollNodeRef.value?.$refs?.customScroll) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      return scrollNodeRef.value?.$refs.customScroll.scrollElement;
    } else return getElement<HTMLElement>(scrollNodeRef.value);
  });

  const prevScrollPos = ref({
    scrollHeight: scrollNode.value?.scrollHeight ?? 0,
    scrollTop: scrollNode.value?.scrollTop ?? 0,
    offsetHeight: scrollNode.value?.offsetHeight ?? 0,
  });

  onBeforeUpdate(() => {
    prevScrollPos.value = {
      scrollHeight: scrollNode.value?.scrollHeight ?? 0,
      scrollTop: scrollNode.value?.scrollTop ?? 0,
      offsetHeight: scrollNode.value?.offsetHeight ?? 0,
    };
  });

  const scrollToBottom = (behavior?: ScrollOptions["behavior"]) => {
    setTimeout(() => {
      if (!scrollNode.value) return;
      if (scrollNode.value.scrollTo) {
        scrollNode.value.scrollTo({
          top: scrollNode.value.scrollHeight - scrollNode.value.offsetHeight,
          behavior: behavior ?? defaultOption?.behavior ?? "auto",
        });
      }
    });
  };
  const keepScrollPos = () => {
    if (!scrollNode.value) return;
    const fromBottomOffset = prevScrollPos.value.scrollHeight - prevScrollPos.value.scrollTop;
    if (scrollNode.value.scrollTo) {
      scrollNode.value.scrollTo(0, scrollNode.value.scrollHeight - fromBottomOffset);
    }
  };
  onUpdated(() => {
    if (scrollNode.value?.scrollHeight !== prevScrollPos.value.scrollHeight) {
      shouldKeepScroll.value && keepScrollPos();
      shouldScrollToBottom.value && scrollToBottom();
    }

    shouldKeepScroll.value = defaultOption?.keepScroll ?? false;
    shouldScrollToBottom.value = defaultOption?.scrollToBottom ?? false;
  });
  return {
    shouldKeepScroll,
    shouldScrollToBottom,
    scrollNode,
    scrollToBottom,
  };
}
